import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    CreditCard as CreditCardIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCreditDashboard, getOverdueAccounts } from '../../services/api';
import './Credit.css';

const CreditDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [overdueData, setOverdueData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const [dashboardRes, overdueRes] = await Promise.all([
                getCreditDashboard(),
                getOverdueAccounts()
            ]);
            
            setDashboardData(dashboardRes.data);
            setOverdueData(overdueRes.data);
        } catch (err) {
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (typeof amount === 'string' && amount.includes('LKR')) return amount;
        return new Intl.NumberFormat('en-LK', {
            style: 'currency',
            currency: 'LKR',
            minimumFractionDigits: 2
        }).format(parseFloat(amount) || 0);
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 3 }}>
                    {error}
                </Alert>
                <Button 
                    variant="outlined" 
                    startIcon={<RefreshIcon />}
                    onClick={fetchDashboardData}
                    sx={{ mt: 2 }}
                >
                    Retry
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4" component="h1">
                        <CreditCardIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        Credit Management Dashboard
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchDashboardData}
                    >
                        Refresh
                    </Button>
                </Box>
                <Typography variant="subtitle1" color="textSecondary">
                    Overview of credit accounts, outstanding balances, and payment status
                </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Total Credit Customers
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold">
                                        {dashboardData?.summary?.total_credit_customers || 0}
                                    </Typography>
                                </Box>
                                <PeopleIcon sx={{ fontSize: 40, color: '#3f51b5' }} />
                            </Box>
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/credit/customers')}
                                sx={{ mt: 1 }}
                            >
                                View All
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Total Outstanding
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold" color="error">
                                        {formatCurrency(dashboardData?.summary?.total_outstanding || 0)}
                                    </Typography>
                                </Box>
                                <PaymentIcon sx={{ fontSize: 40, color: '#f44336' }} />
                            </Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Credit Limit: {formatCurrency(dashboardData?.summary?.total_credit_limit || 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Credit Utilization
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold">
                                        {dashboardData?.summary?.credit_utilization || 0}%
                                    </Typography>
                                </Box>
                                <TrendingUpIcon sx={{ fontSize: 40, color: dashboardData?.summary?.credit_utilization > 80 ? '#f44336' : '#4caf50' }} />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <Chip 
                                    label={dashboardData?.summary?.credit_utilization > 80 ? 'High' : 'Normal'}
                                    size="small"
                                    color={dashboardData?.summary?.credit_utilization > 80 ? 'error' : 'success'}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Overdue Accounts
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                                        {dashboardData?.summary?.overdue_accounts || 0}
                                    </Typography>
                                </Box>
                                <WarningIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                            </Box>
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/credit/overdue')}
                                sx={{ mt: 1 }}
                            >
                                Manage
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Overdue Accounts */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Overdue Accounts
                            </Typography>
                            <Chip 
                                label={`${overdueData?.total_overdue || 0} accounts`}
                                color="warning"
                                size="small"
                            />
                        </Box>
                        
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="center">Days</TableCell>
                                        <TableCell align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {overdueData?.accounts?.slice(0, 5).map((account) => (
                                        <TableRow key={account.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {account.customer_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {account.customer_code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color="error">
                                                    {account.outstanding_balance}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={`${account.days_overdue} days`}
                                                    size="small"
                                                    color="error"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/credit/customers/${account.customer_id}`)}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    
                                    {(!overdueData?.accounts || overdueData.accounts.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography color="textSecondary">No overdue accounts</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {overdueData?.accounts && overdueData.accounts.length > 5 && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Button
                                    size="small"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate('/credit/overdue')}
                                >
                                    View All {overdueData.total_overdue} Accounts
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Top Customers */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Top Customers by Outstanding
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => navigate('/credit/customers')}
                            >
                                View All
                            </Button>
                        </Box>
                        
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Outstanding</TableCell>
                                        <TableCell align="center">Utilization</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dashboardData?.top_customers?.map((customer, index) => (
                                        <TableRow key={customer.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {customer.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {customer.outstanding_balance}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ flexGrow: 1, bgcolor: '#ddd', height: 8, borderRadius: 4 }}>
                                                        <Box 
                                                            sx={{ 
                                                                bgcolor: customer.credit_utilization > 80 ? '#f44336' : 
                                                                        customer.credit_utilization > 60 ? '#ff9800' : '#4caf50',
                                                                width: `${Math.min(customer.credit_utilization || 0, 100)}%`,
                                                                height: '100%',
                                                                borderRadius: 4
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" fontWeight="bold">
                                                        {customer.credit_utilization}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    
                                    {(!dashboardData?.top_customers || dashboardData.top_customers.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography color="textSecondary">No credit customers</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Recent Credit Sales */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Recent Credit Sales
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Monthly: {formatCurrency(dashboardData?.summary?.monthly_credit_sales || 0)}
                            </Typography>
                        </Box>
                        
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Reference</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dashboardData?.recent_sales?.map((sale) => (
                                        <TableRow key={sale.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {sale.reference}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {sale.customer}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {sale.amount}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(sale.date).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={sale.status}
                                                    size="small"
                                                    color={
                                                        sale.status === 'paid' ? 'success' :
                                                        sale.status === 'pending' ? 'warning' :
                                                        sale.status === 'overdue' ? 'error' : 'default'
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    
                                    {(!dashboardData?.recent_sales || dashboardData.recent_sales.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography color="textSecondary">No recent credit sales</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Quick Actions
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CreditCardIcon />}
                            onClick={() => navigate('/credit/customers')}
                            sx={{ py: 1.5 }}
                        >
                            Manage Credit Customers
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            startIcon={<WarningIcon />}
                            onClick={() => navigate('/credit/overdue')}
                            sx={{ py: 1.5 }}
                        >
                            Overdue Accounts
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ReceiptIcon />}
                            onClick={() => navigate('/credit/statements')}
                            sx={{ py: 1.5 }}
                        >
                            Generate Statements
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<TrendingUpIcon />}
                            onClick={() => navigate('/reports/credit')}
                            sx={{ py: 1.5 }}
                        >
                            Credit Reports
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default CreditDashboard;